import React, { useCallback, useContext, useEffect, useState } from "react";
import * as Icons from "react-feather";
import { useForm } from "react-hook-form";
import classNames from "classnames";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "Components/ReactstrapSlim";
import { InputFL, ValidationSummary } from "Components/Inputs";
import { LayoutContext } from "Components/LayoutContext";
import { Module, ModuleCaption } from "Components/Module";
import { CollectionViewModel, PortalClient, CollectionsClient, FavoriteCollectionViewModel, CollectionType } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import { Collection } from "./Collection";
import { CollectionPreferred } from "./CollectionPreferred";
import { useTitle } from "../Layout/Mobility3/Main";
import AccountContext from "../../AccountContext";

interface NameForm {
   name: string | undefined
}

export const Collections: React.FC = () => {
   useTitle("Collections");

   const account = useContext(AccountContext);
   const portalClient = useApiClient(PortalClient);
   const collectionClient = useApiClient(CollectionsClient);   
   const layoutContext = useContext(LayoutContext);

   const [isLoadingEnterprise, setIsLoadingEnterprise] = useState<boolean>(true);
   const [isLoadingMember, setIsLoadingMember] = useState<boolean>(true);
   const [isLoadingShared, setIsLoadingShared] = useState<boolean>(true);
   const [isModalOpen, setIsModalOpen] = useState(false);
   const [preferredCollections, setPreferredCollections] = useState<CollectionViewModel[]>([]);
   const [collections, setCollections] = useState<FavoriteCollectionViewModel[]>([]);
   const [sharedCollections, setSharedCollections] = useState<FavoriteCollectionViewModel[]>([]);
   const [serverError, setServerError] = useState<Array<[string, string]>>([]);

   const NameFormSchema: yup.SchemaOf<NameForm> = yup.object({
      name: yup.string()
         .trim()
         .required("Collection name is required")
         .notOneOf(collections.map(c => c.name), "Collection name must be unique")
         .defined(),
   }).defined();

   const formMethods = useForm<NameForm>({
      mode: "onTouched",
      resolver: yupResolver(NameFormSchema)
   })

   const loadCollectionsData = (abortSignal: AbortSignal) => {
      try {
         portalClient.collections(account.id, abortSignal).then(result => {
            setPreferredCollections(result || []);
            setIsLoadingEnterprise(false);
         });
         collectionClient.collections(abortSignal).then(result => {
            setCollections(result || []);
            setIsLoadingMember(false);
         });
         collectionClient.sharedCollections(abortSignal).then(result => {
            setSharedCollections(result || []);
            setIsLoadingShared(false);
         });
      }
      catch { }
   }

   const addData = async () => {
      try {
         const isValid = await formMethods.trigger();
         if (isValid) {
            const data = formMethods.getValues();
            if (data.name != undefined) {
               const result = await collectionClient.createCollection(data.name);

               if (result) {
                  layoutContext.showMessage(`Collection ${result.name} was successfully created`);
                  collections.push(result);
                  setIsModalOpen(false);
               } else {
                  setServerError([["name", "Unexpected server error"]]);
               }
            }
         }
      } catch (e: any) {
         if (e.errorMessage) {
            setServerError([["name", e.errorMessage]]);
         }
      }
   }

   const deleteCallback = useCallback(async (collectionType: CollectionType | undefined) => {
      const abortController = new AbortController();
      if (collectionType === CollectionType.Regular) {
         const collectionsData = await collectionClient.collections(abortController.signal);
         setCollections(collectionsData || []);
      }
      if (collectionType === CollectionType.Shared) {
         const sharedCollectionsData = await collectionClient.sharedCollections(abortController.signal);
         setSharedCollections(sharedCollectionsData || []);
      }
   }, []);

   const renameCallback = useCallback(async () => {
      const abortController = new AbortController();
      const collectionsData = await collectionClient.collections(abortController.signal);
      setCollections(collectionsData || []);
   }, []);

   const toggleModal = () => setIsModalOpen(!isModalOpen);

   useEffect(() => {
      const abortController = new AbortController();
      loadCollectionsData(abortController.signal);

      return function cleanup() {
         abortController.abort();
      }
   }, []);

   return (<>
      {!isLoadingMember && !isLoadingShared && !isLoadingEnterprise && collections.length == 0 && preferredCollections.length == 0 && sharedCollections.length == 0 &&
         <Module size="lg" className="module-no-collections">
            <ModuleCaption className="title">You don't have any saved collections yet</ModuleCaption>
            <div className="subtitle">Explore spaces nearby and add locations to your collections</div>
            <div className="btn-wrapper">
               <Button color="arctic-20" size="sm" onClick={() => window.open("/search", "_blank")}>Go to search results</Button>
            </div>
         </Module>
      }
      <Module size="lg" loading={isLoadingMember} className={classNames("module-collections", { "d-none": !isLoadingMember && collections.length == 0 })}>
         <ModuleCaption>
            My Collections
            <Button size="sm" color="secondary" className="plus-button" onClick={() => { setIsModalOpen(true) }}><Icons.Plus size={12} /></Button>
         </ModuleCaption>
         <div className="collections-wrapper">
            {collections.map(c => <Collection count={collections.length} key={c.id} collection={c} renameCallback={renameCallback} deleteCallback={deleteCallback} copyCallback={renameCallback} />)}
         </div>
         <Modal isOpen={isModalOpen} toggle={toggleModal}>
            <ModalHeader toggle={toggleModal}>New Collection</ModalHeader>
            <ModalBody className="ls-modal-body">
               <InputFL control={formMethods.control} name="name" placeholder="Collection Name" />
               <ValidationSummary errors={formMethods.formState.errors} serverErrors={serverError} />
            </ModalBody>
            <ModalFooter>
               <Button color="secondary" onClick={() => setIsModalOpen(false)}>Cancel</Button>
               <Button color="primary" onClick={() => addData()}>Add</Button>
            </ModalFooter>
         </Modal>
      </Module>
      <Module size="lg" loading={isLoadingShared || isLoadingEnterprise} className={classNames("module-collections-shared", "module-collections", { "d-none": !(isLoadingShared || isLoadingEnterprise) && preferredCollections.length == 0 && sharedCollections.length == 0 })}>
         <ModuleCaption>Collections shared with me</ModuleCaption>
         <div className="collections-wrapper">
            {sharedCollections.map(sc => <Collection key={sc.id} collection={sc} renameCallback={renameCallback} deleteCallback={deleteCallback} copyCallback={renameCallback} />)}
            <div className="collections-separator"></div>
            {preferredCollections.map(ec => <CollectionPreferred key={ec.id} collection={ec} accountName={account.name} />)}
         </div>
      </Module>
   </>);
}